import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Vote, Loader2, Trophy, RefreshCw, Landmark, Archive } from "lucide-react";

export default function ElectionNight() {
  const [data, setData] = useState(null);
  const [entry, setEntry] = useState(null);
  const [outcomeDone, setOutcomeDone] = useState(null);
  const [runoffDate, setRunoffDate] = useState("");
  const [askRunoff, setAskRunoff] = useState(false);

  const load = () => api.get("/political-os/election-night").then(r => setData(r.data)).catch(e => setData({ race: null, error: errMsg(e) }));
  useEffect(() => { load(); }, []);

  const startEntry = () => {
    const roster = (data.roster || []).map(c => ({ name: c.name, party: c.party, votes: 0 }));
    const existing = data.results?.candidates;
    setEntry({
      candidates: existing?.length ? existing.map(c => ({ ...c })) : roster,
      precincts_reporting: data.results?.precincts_reporting || "",
      status: data.results?.status || "counting",
      winner: data.results?.winner || "",
      source: data.results?.source || "",
    });
  };

  const save = async () => {
    try {
      await api.put("/political-os/election-night/results", { ...entry, candidates: entry.candidates.map(c => ({ ...c, votes: parseInt(c.votes) || 0 })) });
      toast.success("Results updated");
      setEntry(null);
      load();
    } catch (e) { toast.error(errMsg(e, "Could not save results")); }
  };

  const setOutcome = async (outcome, runoff_date) => {
    try {
      const { data: d } = await api.post("/political-os/election-night/outcome", { outcome, runoff_date });
      setOutcomeDone(d);
      setAskRunoff(false);
      toast.success(outcome === "runoff" ? "Runoff race created — campaigns carried forward" : "Outcome recorded & campaign archived");
      load();
    } catch (e) { toast.error(errMsg(e, "Could not record outcome")); }
  };

  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;
  if (!data.race) {
    return <div className="p-6 lg:p-8"><EmptyState icon={Landmark} title="No race attached" description={data.message || "Attach a race to your entity to open Election Night."} /></div>;
  }

  const r = data.results;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="election-night-page">
      <PageHeader eyebrow="Election Night" title={data.race.name}
        subtitle="Enter official results as they're published — TotalReach never calls a race itself; a called race requires the official source." />

      <Card padded={false} data-testid="results-card">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Vote className="w-4 h-4 text-cyan-300" />
            <h3 className="font-display text-lg font-bold">Results</h3>
            {r && <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${r.status === "called" ? "text-emerald-300 border-emerald-400/30" : "text-amber-300 border-amber-400/30"}`}>{r.status}{r.precincts_reporting ? ` · ${r.precincts_reporting} reporting` : ""}</span>}
          </div>
          <Btn variant="secondary" onClick={startEntry} data-testid="enter-results-btn"><RefreshCw className="w-4 h-4" /> {r ? "Update results" : "Enter results"}</Btn>
        </div>
        {r ? (
          <div className="divide-y divide-white/5">
            {r.candidates.sort((a, b) => b.votes - a.votes).map((c, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-4" data-testid={`result-row-${i}`}>
                {r.winner === c.name && <Trophy className="w-4 h-4 text-amber-300 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white">{c.name} <span className="text-[10px] text-slate-500">{c.party}</span></div>
                  <div className="h-1.5 rounded-full bg-white/5 mt-1.5 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${c.pct || 0}%` }} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-mono text-white">{(c.votes || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-slate-500">{c.pct || 0}%</div>
                </div>
              </div>
            ))}
            <div className="px-5 py-2 text-[10px] text-slate-600">Source: {r.source} · updated {r.updated_at?.slice(0, 16).replace("T", " ")} by {r.updated_by}</div>
          </div>
        ) : (
          <div className="px-5 py-6 text-sm text-slate-500">No results entered yet. {data.note}</div>
        )}
      </Card>

      {entry && (
        <Card data-testid="results-entry-card">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Enter official results</div>
          <div className="space-y-2">
            {entry.candidates.map((c, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-sm text-white flex-1 min-w-0 truncate">{c.name}</span>
                <input type="number" value={c.votes} data-testid={`votes-input-${i}`}
                  onChange={e => setEntry(en => { const cs = [...en.candidates]; cs[i] = { ...cs[i], votes: e.target.value }; return { ...en, candidates: cs }; })}
                  className="w-32 bg-slate-900/80 border border-white/15 rounded-lg px-3 py-1.5 text-sm font-mono focus:border-cyan-400/60 focus:outline-none" />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 items-end mt-4">
            <Input label="Precincts reporting" value={entry.precincts_reporting} onChange={e => setEntry(en => ({ ...en, precincts_reporting: e.target.value }))} placeholder="72%" className="w-32" />
            <Select label="Status" value={entry.status} onChange={e => setEntry(en => ({ ...en, status: e.target.value }))} data-testid="results-status-select">
              {["not_started", "counting", "called"].map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </Select>
            {entry.status === "called" && (
              <Select label="Officially declared winner" value={entry.winner} onChange={e => setEntry(en => ({ ...en, winner: e.target.value }))} data-testid="winner-select">
                <option value="">Select…</option>
                {entry.candidates.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </Select>
            )}
            <Input label="Official source (required)" value={entry.source} onChange={e => setEntry(en => ({ ...en, source: e.target.value }))}
              placeholder="Texas Secretary of State results page" className="flex-1 min-w-[220px]" data-testid="results-source-input" />
            <Btn onClick={save} data-testid="save-results-btn">Save results</Btn>
          </div>
        </Card>
      )}

      <Card data-testid="outcome-card">
        <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-1">What's next?</div>
        <p className="text-sm text-slate-400 mb-3">When the race is decided, record the outcome — TotalReach archives a full campaign history snapshot automatically.</p>
        {data.outcome ? (
          <div className="text-sm text-cyan-300 flex items-center gap-2"><Archive className="w-4 h-4" /> Outcome recorded: <b className="uppercase">{data.outcome}</b> — snapshot archived in Campaign History</div>
        ) : (
          <div className="flex gap-2 flex-wrap items-center">
            <button onClick={() => setOutcome("won")} data-testid="outcome-won-btn" className="text-xs px-4 py-2 rounded-full border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 transition-colors">We won</button>
            <button onClick={() => setAskRunoff(a => !a)} data-testid="outcome-runoff-btn" className="text-xs px-4 py-2 rounded-full border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 transition-colors">Runoff</button>
            <button onClick={() => setOutcome("lost")} data-testid="outcome-lost-btn" className="text-xs px-4 py-2 rounded-full border border-white/15 text-slate-400 hover:text-white transition-colors">Campaign complete</button>
            {askRunoff && (
              <div className="flex items-center gap-2" data-testid="runoff-date-row">
                <input type="date" value={runoffDate} onChange={e => setRunoffDate(e.target.value)} data-testid="runoff-date-input"
                  className="bg-slate-900/80 border border-white/15 rounded-lg px-3 py-1.5 text-xs text-slate-300 focus:border-amber-400/60 focus:outline-none" />
                <button onClick={() => setOutcome("runoff", runoffDate || null)} data-testid="confirm-runoff-btn"
                  className="text-xs px-3 py-1.5 rounded-full bg-amber-400/15 border border-amber-400/50 text-amber-300">Create runoff race</button>
              </div>
            )}
          </div>
        )}
        {outcomeDone && (
          <ul className="mt-3 text-xs text-slate-400 list-disc pl-5">
            {outcomeDone.next.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        )}
      </Card>
    </div>
  );
}
